import React from "react";
import { authApi } from "../api";
import type { MeResponse } from "../types";
import { resolvePostLoginRedirect } from "../access";

/**
 * Global auth state using React context.
 * - HttpOnly cookie session, so no token is stored on the client.
 * - On mount, it calls /auth/me to hydrate the current user.
 */
type Ctx = {
  user: MeResponse | null;
  loading: boolean;
  login: (u: string, p: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<MeResponse | null>;
};

const AuthCtx = React.createContext<Ctx>(null as unknown as Ctx);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = React.useState<MeResponse | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async (): Promise<MeResponse | null> => {
    try {
      const resp = await authApi.me();
      // resp is an AxiosResponse, real user data is in resp.data
      const me = (resp as any).data ?? resp;

      setUser(me);
      return me as MeResponse;
    } catch {
      // If /auth/me fails (401, 403, etc.), treat as not authenticated.
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Hydrate user on first mount.
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const login = React.useCallback(
    async (username: string, password: string) => {
      // This should set the HttpOnly cookie on success.
      await authApi.login({ username, password });

      // Refresh the user state from /auth/me.
      const me = await refresh();

      // Restore post-login redirect if any.
      const redirect = resolvePostLoginRedirect(
        sessionStorage.getItem("postLoginRedirect"),
        me?.groups
      );
      sessionStorage.removeItem("postLoginRedirect");

      // Redirect to the target page after successful login.
      window.location.replace(redirect);
    },
    [refresh]
  );

  const logout = React.useCallback(async () => {
    try {
      // Call backend to invalidate the session / clear HttpOnly cookie.
      await authApi.logout();
    } catch {
      // Ignore logout API errors to avoid blocking the UX.
    }

    // Clear in-memory auth state.
    setUser(null);

    // Optional: clear any client-side cached data that depends on the user.
    try {
      window.sessionStorage?.removeItem("postLoginRedirect");
    } catch {
      // Ignore storage errors.
    }

    // IMPORTANT:
    // HttpOnly cookies cannot be cleared from JavaScript.
    // The backend MUST clear them in /auth/logout, otherwise /auth/me
    // will keep returning a valid user and the app will treat the user
    // as still authenticated after reload.

    // Redirect to the login page explicitly.
    try {
      window.location.replace("/login");
    } catch {
      window.location.href = "/login";
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => React.useContext(AuthCtx);
